import {
	PromiseToPay,
	PromiseToPayPermissions,
} from '@lmc-app/core/services/promise-to-pay.service';
import {
	ArrangementType,
	PromiseToPayType,
} from '@lmc-app/shared/enum/promise-to-pay-status.enum';
import { PayoffContext } from './payoff-statement';
import type { PromiseToPayDialogData } from './promise-to-pay-dialog.component';

/** Arrangement types whose amount is quoted from the NLS payoff. */
export const PAYOFF_TYPES: (ArrangementType | null)[] = [
	ArrangementType.PayOff,
	ArrangementType.PayOffExcludeNSF,
];

/**
 * Edit covers every promise in any status; without it there is no Edit action.
 * Shared by the loan page panel and the loan-servicing list so Edit matches in both.
 */
export function canEditPromise(
	permissions: PromiseToPayPermissions | null
): boolean {
	return !!permissions?.canEdit;
}

/** Arrangement type is fixed on edit, so only Pay Off arrangements need a payoff context. */
export function isPayoffArrangement(row: PromiseToPay): boolean {
	return (
		row.type === PromiseToPayType.Arrangement &&
		PAYOFF_TYPES.includes(row.arrangementType)
	);
}

/** Owner candidates are passed only when the user may change owner. */
export function editDialogData(
	row: PromiseToPay,
	permissions: PromiseToPayPermissions | null,
	payoff?: PayoffContext
): PromiseToPayDialogData {
	return {
		promise: row,
		payoff,
		canBackdate: !!permissions?.canBackdate,
		canEdit: !!permissions?.canEdit,
		owners: permissions?.canChangeOwner ? permissions.owners : [],
	};
}

/** `/getLoandetails` meta row, read the same way the loan page reads it. */
export interface LoanDetailsMeta {
	'Insurance Prem'?: number | null;
	'Insurance Tax'?: number | null;
	eff_fees_balance?: number | null;
	origination_date?: string | null;
	loan_group_no?: number | null;
}

export function toPayoffContext(meta: LoanDetailsMeta): PayoffContext {
	const insurance = (meta['Insurance Prem'] ?? 0) + (meta['Insurance Tax'] ?? 0);
	return {
		loanGroupNo: meta.loan_group_no ?? null,
		insurance: isNaN(insurance) ? 0 : Number(insurance.toFixed(2)),
		nsfFees: meta.eff_fees_balance ?? 0,
		originationDate: meta.origination_date ?? null,
	};
}
